export enum FormTemplateScope {
  ORGANIZATION = 'ORGANIZATION',
  PROJECT = 'PROJECT',
  PERSONAL = 'PERSONAL',
  MARKET = 'MARKET',
}

export enum FormVersionStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

export enum FormType {
  PERIODIC = 'PERIODIC',
  ONE_OFF = 'ONE_OFF',
}

export interface FormTemplateSection {
  id: string
  title: string
  description?: string | null
  category?: string | null
  subcategory?: string | null
  position: number
  parentSectionId?: string | null
  metadata?: Record<string, unknown>
}

export interface FormSectionCreateInput {
  title: string
  description?: string | null
  category?: string | null
  subcategory?: string | null
  position?: number
  parentSectionId?: string | null
  metadata?: Record<string, unknown>
}

export type FormSectionUpdatePayload = Partial<FormSectionCreateInput>

export enum FormFieldType {
  SHORT_TEXT = 'SHORT_TEXT',
  LONG_TEXT = 'LONG_TEXT',
  RICH_TEXT = 'RICH_TEXT',
  INTEGER = 'INTEGER',
  DECIMAL = 'DECIMAL',
  PERCENTAGE = 'PERCENTAGE',
  CURRENCY = 'CURRENCY',
  SINGLE_SELECT = 'SINGLE_SELECT',
  MULTI_SELECT = 'MULTI_SELECT',
  DROPDOWN = 'DROPDOWN',
  RATING = 'RATING',
  DATE = 'DATE',
  TIME = 'TIME',
  DATETIME = 'DATETIME',
  DATE_RANGE = 'DATE_RANGE',
  BOOLEAN = 'BOOLEAN',
  CHECKBOX = 'CHECKBOX',
  ATTACHMENT = 'ATTACHMENT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  SIGNATURE = 'SIGNATURE',
  QR_CODE = 'QR_CODE',
  BARCODE = 'BARCODE',
  RANGE = 'RANGE',
  SCALE = 'SCALE',
  PARAGRAPH = 'PARAGRAPH',
}

export interface FormFieldOption {
  label: string
  value: string
  position?: number
  metadata?: Record<string, unknown>
}

export interface FormTemplateField {
  id: string
  key: string
  label: string
  type: FormFieldType
  description?: string | null
  isRequired?: boolean
  isRepeatable?: boolean
  placeholder?: string | null
  helpText?: string | null
  defaultValue?: unknown
  metadata?: Record<string, unknown>
  category?: string | null
  subcategory?: string | null
  position?: number
  sectionId?: string | null
  options?: FormFieldOption[]
}

export type FormInspectionFrequency = 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY'

export type FormFieldUpdatePayload = Partial<Omit<FormTemplateField, 'id'>> & {
  sectionId?: string | null
  options?: FormFieldOption[]
}

export enum FormLogicOperator {
  EQUALS = 'EQUALS',
  NOT_EQUALS = 'NOT_EQUALS',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_OR_EQUAL = 'GREATER_OR_EQUAL',
  LESS_THAN = 'LESS_THAN',
  LESS_OR_EQUAL = 'LESS_OR_EQUAL',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  IS_EMPTY = 'IS_EMPTY',
  IS_NOT_EMPTY = 'IS_NOT_EMPTY',
}

export enum FormLogicAction {
  SHOW = 'SHOW',
  HIDE = 'HIDE',
  ENABLE = 'ENABLE',
  DISABLE = 'DISABLE',
  REQUIRE = 'REQUIRE',
  OPTIONAL = 'OPTIONAL',
  SET_VALUE = 'SET_VALUE',
  CLEAR_VALUE = 'CLEAR_VALUE',
}

export interface FormLogicRule {
  id: string
  triggerFieldId: string
  effectFieldId: string
  operator: FormLogicOperator
  action: FormLogicAction
  comparisonValue?: unknown
  position?: number
  metadata?: Record<string, unknown> | null
}

export interface FormLogicRuleDraft {
  id?: string
  triggerFieldId?: string
  effectFieldId?: string
  operator?: FormLogicOperator
  action?: FormLogicAction
  comparisonValue?: unknown
  isNew?: boolean
}

export interface FormTemplateVersionDetail {
  id: string
  versionNumber: number
  status: FormVersionStatus
  displayName?: string | null
  description?: string | null
  changelog?: string | null
  createdAt?: string
  updatedAt?: string
  sections: FormTemplateSection[]
  fields: FormTemplateField[]
  logicRules: FormLogicRule[]
}

export interface FormTemplateSummary {
  id: string
  name: string
  scopeType: FormTemplateScope
  formType: FormType
  projectId?: string | null
  ownerId?: string | null
  tags: string[]
  isPublished: boolean
  archived: boolean
  activationStartDate?: string | null
  activationEndDate?: string | null
  isAlwaysActive: boolean
  description?: string | null
  latestVersion?: {
    id: string
    versionNumber: number
    status: FormVersionStatus
    displayName?: string | null
    description?: string | null
    createdAt: string
    updatedAt: string
  }
}

export interface FormTemplateDetail extends FormTemplateSummary {
  versions: FormTemplateVersionDetail[]
}

export interface FormTemplateFieldDiff {
  identifier: string
  change: 'added' | 'removed' | 'modified'
  before?: FormTemplateFieldSnapshot
  after?: FormTemplateFieldSnapshot
}

export interface FormTemplateSectionDiff {
  identifier: string
  change: 'added' | 'removed' | 'modified'
  before?: FormTemplateSectionSnapshot
  after?: FormTemplateSectionSnapshot
}

export interface FormTemplateDiffResult {
  fields: FormTemplateFieldDiff[]
  sections: FormTemplateSectionDiff[]
}

export interface FormTemplateFieldSnapshot {
  key: string
  label: string
  type: FormFieldType
  section?: string | null
  category?: string | null
  subcategory?: string | null
  isRequired?: boolean
  options?: Array<{ label: string; value: string }>
}

export interface FormTemplateCreatePayload {
  name: string
  description?: string
  scopeType: FormTemplateScope
  formType?: FormType
  projectId?: string
  ownerId?: string
  tags?: string[]
  activationStartDate?: string | null
  activationEndDate?: string | null
  isAlwaysActive?: boolean
  version?: {
    displayName?: string
    description?: string
    changelog?: string
  }
}

export interface FormTemplateUpdatePayload {
  name?: string
  description?: string | null
  projectId?: string | null
  ownerId?: string | null
  tags?: string[]
  metadata?: Record<string, unknown>
  archived?: boolean
  formType?: FormType
  activationStartDate?: string | null
  activationEndDate?: string | null
  isAlwaysActive?: boolean
}

export interface FormTemplateVersionCreatePayload {
  displayName?: string
  description?: string
  changelog?: string
  status?: FormVersionStatus
  basedOnVersionId?: string
  cloneStructure?: boolean
}

export interface FormTemplatePublishOptions {
  archivePreviousVersions?: boolean
  markTemplatePublished?: boolean
}

export interface FormTemplateSectionSnapshot {
  title: string
  position: number
  parent?: string | null
  category?: string | null
  subcategory?: string | null
}

export interface FormTemplateExportSection {
  id: string
  title: string
  description?: string | null
  category?: string | null
  subcategory?: string | null
  position: number
  parentId?: string | null
  metadata?: Record<string, unknown>
}

export interface FormTemplateExportField {
  key: string
  label: string
  type: FormFieldType
  description?: string | null
  isRequired?: boolean
  isRepeatable?: boolean
  placeholder?: string | null
  helpText?: string | null
  defaultValue?: unknown
  metadata?: Record<string, unknown>
  category?: string | null
  subcategory?: string | null
  position?: number
  sectionId?: string | null
  options?: FormFieldOption[]
}

export interface FormTemplateExportVersion {
  versionNumber: number
  status: FormVersionStatus
  displayName?: string | null
  description?: string | null
  changelog?: string | null
  sections: FormTemplateExportSection[]
  fields: FormTemplateExportField[]
  logicRules: Array<{
    triggerFieldKey: string
    effectFieldKey: string
    operator: FormLogicOperator
    action: FormLogicAction
    comparisonValue?: unknown
  }>
}

export interface FormTemplateExportData {
  template: {
    name: string
    description?: string | null
    scopeType: FormTemplateScope
    projectId?: string | null
    ownerId?: string | null
    tags: string[]
    metadata?: Record<string, unknown>
  }
  version: FormTemplateExportVersion
}

export enum FormInstanceStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REVIEW_REQUIRED = 'REVIEW_REQUIRED',
  OVERDUE = 'OVERDUE',
}

export enum FormSubmissionStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  REOPENED = 'REOPENED',
}

export enum FormAssignmentRole {
  RESPONSIBLE = 'RESPONSIBLE',
  REVIEWER = 'REVIEWER',
  OBSERVER = 'OBSERVER',
}

export enum FormAssignmentStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  COMPLETED = 'COMPLETED',
}

export interface FormAssignment {
  userId: string
  role: FormAssignmentRole
  status: FormAssignmentStatus
  respondedAt?: string | null
  user?: {
    id: string
    name: string
    email: string
    avatar?: string | null
  }
}

export interface FormSubmissionValue {
  fieldId: string
  valueString?: string | null
  valueNumber?: string | null
  valueBoolean?: boolean | null
  valueDate?: string | null
  valueJson?: unknown
  anomalyLevel?: string | null
}

export interface FormSubmissionEntry {
  id: string
  status: FormSubmissionStatus
  submittedById: string
  reviewedById?: string | null
  reviewNotes?: string | null
  submittedAt?: string | null
  reviewedAt?: string | null
  revision: number
  isDraft: boolean
  supersededById?: string | null
  values?: FormSubmissionValue[]
  metadata?: Record<string, unknown>
  submittedBy?: {
    id: string
    name?: string | null
    email?: string | null
  }
  reviewedBy?: {
    id: string
    name?: string | null
    email?: string | null
  } | null
  audits?: FormSubmissionAuditEntry[]
}

export interface ActivityLogEntry {
  timestamp: string
  action: string
  actorId: string
  decision?: string
  status?: string
  notes?: string | null
  submissionId?: string
}

export interface FormSubmissionAuditEntry {
  id: string
  submissionId: string
  revision: number
  actorId: string
  action: string
  diff?: Record<string, unknown>
  metadata?: Record<string, unknown>
  createdAt: string
}
export interface FormScheduleRuleSummary {
  id: string
  name: string
  description?: string | null
  timezone: string
  frequency: string
  interval: number
  startDate: string
  endDate?: string | null
  nextRunAt?: string | null
  assigneeUserIds?: string[]
  assigneeGroupIds?: string[]
  assigneeRoleIds?: string[]
  issueTemplate?: Record<string, unknown> | null
}

export interface FormInstanceDetail {
  id: string
  templateId: string
  title: string
  formType: FormType
  status: FormInstanceStatus
  projectId?: string | null
  dueDate?: string | null
  instructions?: string | null
  metadata?: Record<string, unknown> & { activityLog?: ActivityLogEntry[] }
  assignments: FormAssignment[]
  sequenceNumber?: number | null
  scheduleRuleId?: string | null
  scheduleRule?: FormScheduleRuleSummary | null
  startedAt?: string | null
  completedAt?: string | null
  template?: FormTemplateSummary
  templateVersion: {
    id: string
    versionNumber: number
    status: FormVersionStatus
    displayName?: string | null
    description?: string | null
    sections?: FormTemplateSection[]
    fields?: FormTemplateField[]
  }
  submissions?: FormSubmissionEntry[]
}

export interface FormInstanceSummary extends FormInstanceDetail {}

export enum CalendarEventStatus {
  PLANNED = 'PLANNED',
  CONFIRMED = 'CONFIRMED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  OVERDUE = 'OVERDUE',
}

export type CalendarEventSource = 'form-instance' | 'schedule-rule' | 'manual'

export interface CalendarEventItem {
  id: string
  title: string
  startTime: string
  endTime?: string | null
  status: CalendarEventStatus
  source: CalendarEventSource
  formInstanceId?: string
  scheduleRuleId?: string | null
  projectId?: string | null
  projectName?: string | null
  projectKey?: string | null
  description?: string | null
  metadata?: Record<string, unknown>
}

export interface ReviewNotificationPayload {
  type: 'form.review.result'
  message: string
  data: {
    instanceId: string
    submissionId: string
    decision: 'approve' | 'reject' | 'request_changes'
  }
}

export interface SubmissionRequestedNotification {
  type: 'form.review.requested'
  message: string
  data: {
    instanceId: string
    submissionId: string
  }
}

export type SocketNotification = ReviewNotificationPayload | SubmissionRequestedNotification | ({ type: string } & Record<string, unknown>)
